import { useQueryGetAllBots } from "@apis:bot/bot";
import { HTMLTooltip } from "@components:HTMLTooltip";
import { SchemaError, SchemaForm } from "@components:SchemaForm";
import {
  Box,
  Chip,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { FormsInputProps } from "@pages:settings/Tenent/Bots/forms";
import { cloneDeep, debounce, isEqual } from "lodash";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { IoMdCheckmarkCircle } from "react-icons/io";
import { MdCode, MdViewAgenda } from "react-icons/md";
import useUndo from "use-undo";

import { aiSettingsSchema } from "./schema";

const AISettingsJson = lazy(() =>
  import("./AISettingsJson").then((module) => ({
    default: module.AISettingsJson,
  })),
);

type AiSettingsValue = FormsInputProps["ai_settings"];

/*
 * Undo/redo over `ai_settings`: a snapshot is recorded after each burst of changes,
 * from either view, and restored by writing it back to the form.
 */
const useSettingsHistory = () => {
  const { watch, setValue, getValues } = useFormContext<FormsInputProps>();
  // A copy: the form changes its values object in place
  const [initial] = useState(() => cloneDeep(getValues("ai_settings")));
  const [
    { past, present, future },
    { set, reset, undo, redo, canUndo, canRedo },
  ] = useUndo<AiSettingsValue>(initial);
  const restoring = useRef(false);
  const pending = useRef<{ flush: () => void }>(null);

  useEffect(() => {
    const record = debounce((value: AiSettingsValue) => {
      set(cloneDeep(value));
    }, 500);
    pending.current = record;

    const subscription = watch((values, { name }) => {
      if (name && !name.startsWith("ai_settings")) return;

      if (restoring.current) {
        restoring.current = false;
        return;
      }

      // No field name means the whole form was reset (e.g. the bot loaded): start over
      if (!name) {
        record.cancel();
        reset(cloneDeep(values.ai_settings as AiSettingsValue));
        return;
      }

      record(values.ai_settings as AiSettingsValue);
    });

    return () => {
      subscription.unsubscribe();
      record.cancel();
    };
  }, [watch, set, reset]);

  const restore = (value: AiSettingsValue) => {
    if (isEqual(value, getValues("ai_settings"))) return;
    restoring.current = true;
    setValue("ai_settings", cloneDeep(value), { shouldDirty: true });
  };

  // Records an edit that still waits for its snapshot; tells whether there was one
  const recordPending = () => {
    const edited = !isEqual(getValues("ai_settings"), present);
    pending.current?.flush();
    return edited;
  };

  return {
    canUndo,
    canRedo,
    undo: () => {
      const target = recordPending() ? present : past[past.length - 1];
      undo();
      restore(target);
    },
    redo: () => {
      // Recording a new edit clears what could be redone
      if (recordPending()) return;
      const target = future[0];
      redo();
      restore(target);
    },
  };
};

export const AISettings = ({
  botId,
  stored,
  errors,
}: {
  botId?: string;
  stored?: unknown;
  errors: SchemaError[];
}) => {
  const { t } = useTranslation();
  const { setValue } = useFormContext<FormsInputProps>();
  const [viewMode, setViewMode] = useState<"json" | "form">("form");
  const history = useSettingsHistory();
  const { data: listBots, isLoading } = useQueryGetAllBots();

  return (
    <Box>
      <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between" }}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, next: "json" | "form" | null) => {
            if (next !== null) setViewMode(next);
          }}
          sx={{
            "& .MuiToggleButton-root": {
              px: 3,
              py: 1,
              textTransform: "none",
              fontWeight: 500,
              "&.Mui-selected": {
                backgroundColor: "primary.main",
                color: "white",
                "&:hover": { backgroundColor: "primary.dark" },
              },
            },
          }}
        >
          <ToggleButton value="form">
            <MdViewAgenda style={{ marginRight: 8 }} />
            {t("tenant.aiSettings.formView")}
          </ToggleButton>
          <ToggleButton value="json">
            <MdCode style={{ marginRight: 8 }} />
            {t("tenant.aiSettings.jsonView")}
          </ToggleButton>
        </ToggleButtonGroup>

        {errors.length > 0 ? (
          <HTMLTooltip
            title={
              <>
                {errors.map((error) => (
                  <Typography
                    key={`${error.path}:${error.message}`}
                    sx={{ display: "block", marginY: "8px" }}
                  >
                    {error.path ? `${error.path}: ` : ""}
                    {error.message}
                  </Typography>
                ))}
              </>
            }
          >
            <Chip
              label={t("tenant.aiSettings.invalid")}
              sx={{
                background: "#874b4b40",
                color: "#874b4b",
                height: "2.25rem",
              }}
              icon={<IoMdCheckmarkCircle size={20} color="white" />}
            />
          </HTMLTooltip>
        ) : (
          <Chip
            label={t("tenant.aiSettings.valid")}
            sx={{
              background: "#3cbd6a40",
              color: "#3cbd6a",
              height: "2.25rem",
            }}
            icon={<IoMdCheckmarkCircle size={20} color="white" />}
          />
        )}
      </Box>

      {viewMode === "json" ? (
        <Suspense fallback={<CircularProgress size={24} />}>
          <AISettingsJson
            onChange={(data) =>
              setValue("ai_settings", data as AiSettingsValue, {
                shouldDirty: true,
              })
            }
            botId={botId}
            listBots={listBots}
            isLoading={isLoading}
            {...history}
          />
        </Suspense>
      ) : (
        <SchemaForm
          schema={aiSettingsSchema}
          rootPath="ai_settings"
          errors={errors}
          stored={stored}
        />
      )}
    </Box>
  );
};
